import type { FunctionComponent } from 'react';
import React, { useCallback, useMemo } from 'react';

import type UIAnalyticsEvent from '@atlaskit/analytics-next/UIAnalyticsEvent';
import AnalyticsListener from '@atlaskit/analytics-next/AnalyticsListener';
import { FabricChannel } from '@atlaskit/analytics-listeners/types';
import { getNonProductionBuildAnalyticsAttributes } from '@atlaskit/editor-common/utils/is-non-production-build';

import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { useIsSSROrHydrating } from '@confluence/ssr-utilities/entry-points/useIsSSROrHydrating.ts';

import { extendPayloadAttributes } from './helpers.ts';
import { getConfluenceNonProductionBuild } from './isNonProductionBuild.ts';

interface RendererAnalyticsListenerProps {
	objectId?: string;
	objectType?: string;
	source?: string;
	children?: React.ReactNode;
}

export const RendererAnalyticsListener: FunctionComponent<RendererAnalyticsListenerProps> = (
	props,
) => {
	const { objectId, objectType, children, source } = props;
	// setting default to development so in tests it doesn't fail due to being undefined
	const { environment } = useSessionData() || {};
	// setting default to development so in tests it doesn't fail due to being undefined
	const safeEnvironment = environment || 'DEVELOPMENT';
	const renderServerPlaceholder = useIsSSROrHydrating();
	const nonProductionBuildAnalyticsAttributes = useMemo(
		() =>
			getNonProductionBuildAnalyticsAttributes(() =>
				renderServerPlaceholder ? undefined : getConfluenceNonProductionBuild(),
			),
		[renderServerPlaceholder],
	);

	const handleEvent = useCallback(
		(event: UIAnalyticsEvent) => {
			const instanceName: string | undefined = safeEnvironment;

			event.update((payload) => {
				let newPayload = extendPayloadAttributes(payload, {
					objectId,
					objectType,
					rendererSource: source,
				});

				if (instanceName) {
					newPayload = extendPayloadAttributes(newPayload, {
						instance: instanceName,
					});
				}
				if (nonProductionBuildAnalyticsAttributes.isNonProductionBuild !== undefined) {
					newPayload = extendPayloadAttributes(newPayload, nonProductionBuildAnalyticsAttributes);
				}
				return newPayload;
			});
		},
		[safeEnvironment, objectId, objectType, source, nonProductionBuildAnalyticsAttributes],
	);

	return (
		<AnalyticsListener onEvent={handleEvent} channel={FabricChannel.editor}>
			<AnalyticsListener onEvent={handleEvent} channel={FabricChannel.media}>
				{children}
			</AnalyticsListener>
		</AnalyticsListener>
	);
};
